import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlerts } from "../../contexts/AlertsContext";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import ButtonType from "../common/button/ButtonType";
import ImgUtil from "../../utils/ImgUtil";
import _3DInstance from "../../model/_3DInstance";
import TexturesList from "../textures/TexturesList";
import TimeUtil from "../../utils/TimeUtil";
import EntityStats from "../../model/EntityStats";
import ChemiNovaEntityType from "../../model/enums/ChemiNovaEntityType";
import AnnotationsList from "../annotations/AnnotationsList";
import DownloadCode from "../../model/DownloadCode";
import { Tooltip } from "react-tooltip";
import PagedData from "../../model/PagedData";
import Texture from "../../model/Texture";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import { useAuth } from "../../contexts/AuthContext";
import ReportButton from "../common/ReportButton";

function _3dInstanceDetails() {
  // read the ID from the url
  const { id } = useParams();

  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<_3DInstance>(_3DInstance.EmptyInstance());
  // information about the default texture
  const [defaultTexture, setDefaultTexture] = useState<Texture | undefined>(
    undefined,
  );
  // set the state to track any error messages received from the server
  const [errorMessage, setErrorMessage] = useState("");
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let _3dInstance: _3DInstance | undefined = undefined;
        if (id) {
          _3dInstance = await apiClient?.get<_3DInstance>({
            url: APIEndPoints._3DINSTANCES.ONE(parseInt(id)),
          });
          setData(_3dInstance);

          const allTextures = await apiClient?.get<PagedData<Texture[]>>({
            url: APIEndPoints.TEXTURES.ALL(parseInt(id)),
          });
          // detect the default texture
          const auxTextures = allTextures.items.filter(
            (item) => item.gltf_name,
          );
          // should be one and only one
          if (auxTextures.length < 1) {
            setErrorMessage(
              "This scan has no default texture. Delete this record and repeat the add process.",
            );
          } else {
            setDefaultTexture(auxTextures[0]);
          }
        }
      } catch (error: any) {
        if (!error.processed) {
          setErrorMessage("Something went wrong. Try again later!");
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id]);

  const getDownloadCode = async (id: number | undefined) => {
    if (!id) {
      return;
    }
    const responseData = await apiClient?.get<DownloadCode>({
      url: APIEndPoints.DOWNLOADS.CODE(id),
      requiresToken: true,
    });
    console.log(responseData);
    window.location.href = APIEndPoints.DOWNLOADS.FILE(
      responseData.download_code,
    );
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints._3DINSTANCES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections.E3DS,
            id: data.id_e3dmodel,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the record",
          });
        }
      });
  };

  if (errorMessage) {
    alertsManager.showDialogWindow({
      type: DialogWindowType.ERROR,
      message: errorMessage,
      confirmLabel: "Ok",
      onConfirm: () => setErrorMessage(""),
    });
  }

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() || authManager.isAdmin());

  const canAddData =
    !EmbeddingToolInfo.isChemiTool() &&
    (authManager.isAdmin() ||
      authManager.isSpecialist() ||
      authManager.isBoardMember());

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="3D instance icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo className="span-3">
              {AppIcons.GUID} GUID: <span>{data.GUID}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.CLOCK}
              created on: <span>{TimeUtil.display_date(data.created_on)}</span>
            </ButtonInfo>
            <div></div>
            <div></div>
            <ButtonInfo>
              {AppIcons.DETAIL_LEVEL}
              detail level: <span>{data.detail_level}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.TEXTURES}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.TEXTURE_LAYER,
                )}
              </span>
              textures
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ANNOTATIONS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ANNOTATION,
                )}
              </span>
              annotations
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.ALTERATION_FORMS}
              <span>
                {EntityStats.getStats(
                  data.counts,
                  ChemiNovaEntityType.ALTERATION_EVENT,
                )}
              </span>
              alteration events
            </ButtonInfo>
          </div>

          <div className="flex items-start mt-5">
            {canEdit && (
              <Button
                label="Delete this record"
                purpose={ButtonAction.DELETE}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this record?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Yes, delete record",
                    onConfirm: handleDelete,
                  });
                }}
              />
            )}

            <ReportButton
                entityType="3DInstance"
                entityName={data.name}
                entityId={data.id || 0}
            />
          </div>
        </div>
      </div>
      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Record details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections._3DINSTANCES,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>

        <div className="details-section-content">
          <table className="details-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GUID</td>
                <td>{data.GUID ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Detail level</td>
                <td>{data.detail_level ?? "Not found"}</td>
              </tr>
              <tr>
                <td>License type</td>
                <td>{data.license_type ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Sensor configuration</td>
                <td>{data.sensor_configuration ?? "Not found"}</td>
              </tr>
              <tr>
                <td>GLTF file</td>
                <td>
                  {defaultTexture?.gltf_name ? (
                    <a
                      className="link-download block mt-2 mb-2"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        getDownloadCode(defaultTexture.id_gltf_file!);
                      }}
                      download={defaultTexture.gltf_name}
                    >
                      {defaultTexture.gltf_name}
                    </a>
                  ) : (
                    "Not found"
                  )}
                </td>
              </tr>
              <tr>
                <td>Binary file</td>
                <td>
                  {data.bin_name ? (
                    <a
                      className="link-download block mt-2 mb-2"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        getDownloadCode(data.id_file);
                      }}
                      download={data.bin_name}
                    >
                      {data.bin_name}
                    </a>
                  ) : (
                    "Not found"
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Texture layers section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>
            Textures
            <span className="tooltip-hint">
              <a data-tooltip-id="form-tooltip" data-tooltip-place="right">
                {AppIcons.INFO}
              </a>
              <Tooltip id="form-tooltip">
                Refers to data acquired from a specific sensor and represented
                <br />
                over a specific 3D representation (surface) of the Cultural
                <br />
                Heritage Asset.
              </Tooltip>
            </span>
          </h2>
          {canAddData && (
            <Button
              label="Add texture"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections._3DINSTANCES,
                    id: data.id,
                    additionalSections: [AppSections.TEXTURES],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )}
        </div>
        <div className="details-section-content">{<TexturesList />}</div>
      </div>

      {/* Annotations Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Annotations</h2>
          {/*canAddData && (
            <Button
              label="Create annotation - TEST ONLY"
              purpose={ButtonAction.CREATE}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections._3DINSTANCES,
                    id: data.id,
                    additionalSections: [AppSections.ANNOTATIONS],
                    subsection: AppSubSections.CREATE,
                  }),
                )
              }
            />
          )*/}
        </div>
        <div className="details-section-content">
          <AnnotationsList />
        </div>
      </div>

      {/* Connected 3D instances section */}
      {/*<div className="details-section">
        <div className="details-section-title">
          <h2>Connected 3D instances</h2>
          <Button
            label="Add connection"
            purpose={ButtonAction.CREATE}
            type={ButtonType.ICON_TEXT}
            onClick={() =>
              navigate(
                SETTINGS.get_path({
                  section: AppSections._3DINSTANCES,
                  id: data.id,
                  additionalSections: [AppSections.SPATIAL_RELATIONS],
                  subsection: AppSubSections.CREATE,
                })
              )
            }
          />
        </div>
        <div className="details-section-content">
          {<SpatialRelationsList />}
        </div>
      </div>*/}
    </div>
  );
}

export default _3dInstanceDetails;
